import { useRef, useState } from "react";
import { useFieldArray, useForm, type FieldErrors } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import type { CrmContact, CrmRecordSummary, CrmRecordType } from "@/lib/api/crm/masters";
import { crmRulesQueryKey, type CrmRules } from "@/lib/api/crm/rules";
import type { AvailableOnboardingInstanceInfo } from "@/lib/api/customer";
import { formatPhoneAsYouType } from "@/lib/validation/phoneNumber";
import { recordTypeLabels } from "../recordTypes";
import { AddressesSection } from "./components/AddressesSection";
import { BaseFieldsSection } from "./components/BaseFieldsSection";
import { ContactsSection } from "./components/ContactsSection";
import { FormErrorBanner } from "./components/FormErrorBanner";
import { TypeFieldsSection } from "./components/TypeFieldsSection";
import { onboardingPrefill } from "./onboardingPrefill";
import {
  genericFormSubmitError,
  invalidPhonesBanner,
  isInvalidPhoneError,
  type FormBanner,
  type FormSubmitError,
} from "./recordFormErrors";
import { buildRecordFormSchema, createsCustomer, recordTypeFields, type RecordFormValues } from "./recordFormSchema";
import { emptyAddressRow, emptyContactRow, presetOwnerFor, typeFieldsFor } from "./recordFormValues";
import { splitAllowedTagIds, tagNames, type CrmTagTarget } from "./tagOptions";

/** Notes key for the record's own tags (rows use their field array key). */
const RECORD_TAGS_NOTE = "record";

const contactKindNames: Record<CrmContact["kind"], string> = { phone: "a phone number", email: "an email address" };

/** The form shared by the create and edit pages and the customer dialogs. */
export function RecordForm({
  mode,
  rules,
  defaultValues,
  groupRecords,
  groupName,
  lockedFields,
  allowNewGroup = false,
  allowedRecordTypes = null,
  presetOwnerId,
  onGroupChange,
  hasChanges,
  onSubmit,
  onCancel,
  onReload,
}: {
  mode: "create" | "update";
  rules: CrmRules;
  defaultValues: RecordFormValues;
  /** Records in the chosen group: "Belongs to" candidates and names. */
  groupRecords: CrmRecordSummary[];
  groupName: string | null;
  lockedFields: { master_id: boolean };
  /** Create: offer creating a new record group with the record. */
  allowNewGroup?: boolean;
  /** Create: only these record types are offered; null offers every type. */
  allowedRecordTypes?: readonly CrmRecordType[] | null;
  /** Create: the record the new one should belong to when the chosen type allows it (or something above it). */
  presetOwnerId?: string | null;
  onGroupChange?: (masterId: string) => void;
  /** Edit: whether the values differ from the stored record. Save is disabled while they don't. */
  hasChanges?: (values: RecordFormValues) => boolean;
  /** Resolves to null on success, else where to show the error. */
  onSubmit: (values: RecordFormValues) => Promise<FormSubmitError | null>;
  onCancel: () => void;
  /** "Load their changes": refetch and reset the form, discarding edits. */
  onReload?: () => Promise<void>;
}) {
  const queryClient = useQueryClient();
  const formRef = useRef<HTMLFormElement>(null);
  const [banner, setBanner] = useState<FormBanner | null>(null);
  const [tagNotes, setTagNotes] = useState<Record<string, string>>({});
  const [isReloading, setIsReloading] = useState(false);

  const form = useForm<RecordFormValues>({
    // The schema follows the chosen type: only its fields exist, so only they validate.
    resolver: (formValues, context, options) =>
      zodResolver(buildRecordFormSchema(rules, formValues.record_type, mode))(formValues, context, options),
    mode: "onTouched",
    defaultValues,
  });
  const contactRows = useFieldArray({ control: form.control, name: "contacts", keyName: "key" });
  const addressRows = useFieldArray({ control: form.control, name: "addresses", keyName: "key" });

  const recordType = form.watch("record_type");
  const values = form.watch();
  const isSaving = form.formState.isSubmitting;
  const isUnchanged = hasChanges ? !hasChanges(values) : false;

  const setNote = (key: string, removedTagIds: string[], from: string): void => {
    setTagNotes((notes) => {
      const { [key]: _previous, ...rest } = notes;
      return removedTagIds.length > 0
        ? { ...rest, [key]: `Removed tags that can't be used on ${from}: ${tagNames(rules, removedTagIds)}` }
        : rest;
    });
  };

  const clearNote = (key: string): void => setNote(key, [], "");

  /** Keeps only the tags still allowed on the target; returns the removed ones. */
  const keepAllowedTags = (
    name: "tag_ids" | `contacts.${number}.tag_ids` | `addresses.${number}.tag_ids`,
    target: CrmTagTarget,
  ): string[] => {
    const { allowed, removed } = splitAllowedTagIds(rules, form.getValues(name), target);
    if (removed.length > 0) form.setValue(name, allowed, { shouldDirty: true });
    return removed;
  };

  const handleRecordTypeChange = (nextType: CrmRecordType): void => {
    if (nextType === form.getValues("record_type")) return;
    const label = recordTypeLabels[nextType].toLowerCase();
    const typeName = `${/^[aeiou]/.test(label) ? "an" : "a"} ${label}`;
    form.setValue("record_type", nextType, { shouldDirty: true });
    form.setValue("fields", typeFieldsFor(rules, nextType, form.getValues("fields")), { shouldDirty: true });
    form.setValue("owner_id", presetOwnerFor(rules, nextType, presetOwnerId ?? null, groupRecords), { shouldDirty: true });
    // Only customer types take an onboarding instance; what it filled in stays as typed.
    if (!createsCustomer(rules, nextType)) form.setValue("onboarding", null, { shouldDirty: true });
    form.clearErrors(["fields", "owner_id", "client_id", "onboarding", "record_type"]);

    setNote(RECORD_TAGS_NOTE, keepAllowedTags("tag_ids", { type: "record", recordType: nextType }), typeName);
    form.getValues("contacts").forEach((contact, index) => {
      const removed = keepAllowedTags(`contacts.${index}.tag_ids`, { type: "contact", recordType: nextType, contactKind: contact.kind });
      setNote(contactRows.fields[index].key, removed, typeName);
    });
    form.getValues("addresses").forEach((_, index) => {
      const removed = keepAllowedTags(`addresses.${index}.tag_ids`, { type: "address", recordType: nextType });
      setNote(addressRows.fields[index].key, removed, typeName);
    });
  };

  /**
   * A new group is created with the record, so it has no records yet: the chosen group and owner clear, and
   * a type that must belong to a record is cleared too. The group names start as the record's name.
   */
  const handleNewGroupChange = (checked: boolean): void => {
    const longName = form.getValues("long_name").trim();
    // Not marked dirty, so Group short name keeps following Group name until it's typed in.
    form.setValue("new_group", checked ? { long_name: longName, short_name: longName } : null);
    form.setValue("master_id", "", { shouldDirty: true });
    form.setValue("owner_id", "", { shouldDirty: true });
    const currentType = form.getValues("record_type");
    if (checked && currentType && rules.record_types[currentType].owner_types) {
      form.setValue("record_type", "", { shouldDirty: true });
      form.setValue("fields", typeFieldsFor(rules, "", form.getValues("fields")), { shouldDirty: true });
      form.setValue("onboarding", null, { shouldDirty: true });
    }
    form.clearErrors(["master_id", "new_group", "owner_id", "record_type"]);
    onGroupChange?.("");
  };

  /** Fills the form from the picked instance (see `onboardingPrefill`); clearing it keeps what was filled in. */
  const handleOnboardingChange = (instance: AvailableOnboardingInstanceInfo | null): void => {
    const currentType = form.getValues("record_type");
    if (!instance || !currentType) {
      form.setValue("onboarding", null, { shouldDirty: true });
      form.clearErrors("onboarding");
      return;
    }
    const prefill = onboardingPrefill(rules, currentType, form.getValues(), instance);
    form.setValue("onboarding", prefill.onboarding, { shouldDirty: true });
    form.clearErrors("onboarding");
    if (prefill.long_name !== null) {
      form.setValue("long_name", prefill.long_name, { shouldDirty: true, shouldValidate: true });
      // Short name follows Name until it's typed in, as when typing the name.
      if (!form.getFieldState("short_name").isDirty) form.setValue("short_name", prefill.long_name);
    }
    form.setValue("fields", prefill.fields, { shouldDirty: true });
    contactRows.replace(prefill.contacts);
    addressRows.replace(prefill.addresses);
  };

  const handleContactKindChange = (index: number, kind: CrmContact["kind"]): void => {
    form.setValue(`contacts.${index}.kind`, kind, { shouldDirty: true, shouldTouch: true });
    // Digits typed while the row was an email get the phone formatting; an email address is left for validation to flag.
    const value = form.getValues(`contacts.${index}.value`);
    if (kind === "phone" && !value.includes("@")) {
      form.setValue(`contacts.${index}.value`, formatPhoneAsYouType(value), { shouldDirty: true });
    }
    const currentType = form.getValues("record_type");
    const removed = currentType
      ? keepAllowedTags(`contacts.${index}.tag_ids`, { type: "contact", recordType: currentType, contactKind: kind })
      : [];
    setNote(contactRows.fields[index].key, removed, contactKindNames[kind]);
    // Re-check the value against the new kind once it has been touched.
    if (form.getFieldState(`contacts.${index}.value`).isTouched || form.formState.isSubmitted) {
      void form.trigger(`contacts.${index}.value`);
    }
  };

  const setTags = (
    name: "tag_ids" | `contacts.${number}.tag_ids` | `addresses.${number}.tag_ids`,
    noteKey: string,
    tagIds: string[],
  ): void => {
    form.setValue(name, tagIds, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    clearNote(noteKey);
  };

  const scrollToFirstError = (): void => {
    window.setTimeout(() => {
      formRef.current?.querySelector('[aria-invalid="true"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 0);
  };

  /** Blocked by the form schema: phones that can't be converted to E.164 also get a banner. */
  const handleInvalid = (errors: FieldErrors<RecordFormValues>): void => {
    const invalidPhones = form.getValues("contacts").filter((_, index) =>
      isInvalidPhoneError(`contacts.${index}.value`, errors.contacts?.[index]?.value?.message)).length;
    if (invalidPhones > 0) {
      setBanner(invalidPhonesBanner(invalidPhones));
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    scrollToFirstError();
  };

  const handleValid = async (submitted: RecordFormValues): Promise<void> => {
    setBanner(null);
    let error: FormSubmitError | null;
    try {
      error = await onSubmit(submitted);
    } catch {
      error = genericFormSubmitError;
    }
    if (!error) return;
    for (const fieldError of error.fieldErrors) {
      form.setError(fieldError.name, { type: "server", message: fieldError.message });
    }
    if (error.refetchRules) {
      void queryClient.invalidateQueries({ queryKey: crmRulesQueryKey });
    }
    setBanner(error.banner);
    if (error.banner) {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      scrollToFirstError();
    }
  };

  const handleReload = async (): Promise<void> => {
    if (!onReload) return;
    setIsReloading(true);
    try {
      await onReload();
    } finally {
      setIsReloading(false);
    }
  };

  return (
    <Form {...form}>
      <form
        ref={formRef}
        noValidate
        onSubmit={form.handleSubmit(handleValid, handleInvalid)}
        className="scroll-mt-6 space-y-6"
        data-testid="crm-record-form"
      >
        {banner && (
          <FormErrorBanner
            banner={banner}
            onReload={onReload ? () => { void handleReload(); } : undefined}
            onDismiss={() => setBanner(null)}
            isReloading={isReloading}
          />
        )}

        <div className="space-y-6 rounded-xl border bg-white p-5 shadow-sm">
          <BaseFieldsSection
            mode={mode}
            rules={rules}
            groupRecords={groupRecords}
            groupName={groupName}
            isGroupLocked={lockedFields.master_id}
            allowNewGroup={allowNewGroup}
            allowedRecordTypes={allowedRecordTypes}
            disabled={isSaving}
            recordTagNote={tagNotes[RECORD_TAGS_NOTE] ?? null}
            onGroupChange={(masterId: string) => {
              // Owners belong to the group, so a new group needs a new owner.
              form.setValue("owner_id", "");
              onGroupChange?.(masterId);
            }}
            onRecordTypeChange={handleRecordTypeChange}
            onRecordTagsChange={(tagIds: string[]) => setTags("tag_ids", RECORD_TAGS_NOTE, tagIds)}
            onOnboardingChange={handleOnboardingChange}
            onNewGroupChange={handleNewGroupChange}
          />
        </div>

        {recordTypeFields(rules, recordType).length > 0 && (
          <div className="rounded-xl border bg-white p-5 shadow-sm">
            <TypeFieldsSection fields={recordTypeFields(rules, recordType)} disabled={isSaving} />
          </div>
        )}

        <div className="rounded-xl border bg-white p-5 shadow-sm">
          <ContactsSection
            rows={contactRows.fields}
            rules={rules}
            recordType={recordType}
            notes={tagNotes}
            disabled={isSaving}
            onAdd={() => contactRows.append(emptyContactRow(), { shouldFocus: false })}
            onRemove={(index: number) => {
              clearNote(contactRows.fields[index].key);
              contactRows.remove(index);
            }}
            onKindChange={handleContactKindChange}
            onTagsChange={(index: number, tagIds: string[]) =>
              setTags(`contacts.${index}.tag_ids`, contactRows.fields[index].key, tagIds)}
          />
        </div>

        <div className="rounded-xl border bg-white p-5 shadow-sm">
          <AddressesSection
            rows={addressRows.fields}
            rules={rules}
            recordType={recordType}
            notes={tagNotes}
            disabled={isSaving}
            onAdd={() => addressRows.append(emptyAddressRow(), { shouldFocus: false })}
            onRemove={(index: number) => {
              clearNote(addressRows.fields[index].key);
              addressRows.remove(index);
            }}
            onTagsChange={(index: number, tagIds: string[]) =>
              setTags(`addresses.${index}.tag_ids`, addressRows.fields[index].key, tagIds)}
          />
        </div>

        <div className="flex items-center justify-end gap-3">
          {isUnchanged && <span className="text-sm text-gray-500" data-testid="crm-form-no-changes">No changes to save</span>}
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSaving}>Cancel</Button>
          <Button type="submit" disabled={isSaving || isUnchanged} data-testid="crm-form-submit">
            {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            {mode === "create" ? "Save record" : "Save changes"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
